import { useState, useMemo } from 'react';

export interface UseTableSearchProps<T> {
  data: T[] | undefined;
  searchFields: (keyof T)[];
  serverSideSearch?: {
    value: string;
    onChange: (value: string) => void;
  };
}

export function useTableSearch<T extends Record<string, unknown>>({
  data,
  searchFields,
  serverSideSearch,
}: UseTableSearchProps<T>) {
  const [localSearchQuery, setLocalSearchQuery] = useState('');

  // Use server-side search if provided, otherwise use local search
  const searchQuery = serverSideSearch?.value ?? localSearchQuery;
  const setSearchQuery = serverSideSearch?.onChange ?? setLocalSearchQuery;

  const filteredData = useMemo(() => {
    if (!data || serverSideSearch) {
      // If server-side search is enabled, don't filter client-side
      return data || [];
    }

    if (!searchQuery || searchFields.length === 0) {
      return data;
    }

    const searchStr = searchQuery.toLowerCase();
    return data.filter((row) =>
      searchFields.some((field) => {
        const fieldValue = row[field];
        // Extract .value or .formattedValue if present (for cell objects)
        let searchableValue: unknown = fieldValue;
        if (
          fieldValue &&
          typeof fieldValue === 'object' &&
          ('value' in fieldValue || 'formattedValue' in fieldValue)
        ) {
          const cellObj = fieldValue as {
            value?: unknown;
            formattedValue?: unknown;
          };
          searchableValue =
            'formattedValue' in cellObj && cellObj.formattedValue !== undefined
              ? cellObj.formattedValue
              : 'value' in cellObj && cellObj.value !== undefined
                ? cellObj.value
                : fieldValue;
        }
        return searchableValue?.toString()?.toLowerCase().includes(searchStr);
      }),
    );
  }, [data, searchQuery, searchFields, serverSideSearch]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);
  };

  return {
    searchQuery,
    setSearchQuery,
    filteredData,
    handleSearchChange,
  };
}
